// ===== POC Cuidados Paliativos (PNCP) - Rede de CP (Telas 7, 8 e 9) e relatório do hospital =====
// Substitui mockPalliativeNetwork.js, mockPalliativeTeams.js e mockPalliativeCare.js.
// Converte as respostas da API para o formato que as telas já usavam.

import moment from "moment";
import { apiGet, query } from "./http";
import { competenceInfo } from "./susProductionApi";

// Valores de repasse mensal da PNCP (Portaria GM/MS 3.681/2024): regra pública, não vem da API.
export const FUNDING = {
  matrix: 65000,
  assistencial: 44200
};

const SHORT_MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

// Situação da equipe (art. 34) vinda da API → selo da tela.
const SITUATIONS = {
  risco_desabilitacao: { tone: "critical", rank: 3, atRisk: true, label: "Risco de desabilitação" },
  abaixo_minimo: { tone: "critical", rank: 2, atRisk: true, label: "Abaixo do mínimo" },
  atencao: { tone: "warning", rank: 1, atRisk: false, label: "Atenção ao registro" },
  em_dia: { tone: "good", rank: 0, atRisk: false, label: "Em dia" }
};

export function situationStatus(situation, team, rules) {
  const status = { ...(SITUATIONS[situation] || SITUATIONS.em_dia) };
  if (situation === "risco_desabilitacao") {
    status.reason = team.daysSinceRecord == null
      ? `Nenhum registro de produção. Limite de ${rules.maxDaysWithoutRecord} dias sem registro.`
      : `${team.daysSinceRecord} dias sem registro de produção. Limite de ${rules.maxDaysWithoutRecord} dias.`;
  } else if (situation === "abaixo_minimo") {
    status.reason = `${team.production} de ${rules.minimumPerMonth} procedimentos no mês. O repasse do mês fica suspenso.`;
  }
  return status;
}

// Produção dos últimos 6 meses até a competência, com 0 nos meses sem registro.
function lastSixMonths(totals, competence) {
  const byCompetence = {};
  (totals || []).forEach((t) => {
    byCompetence[String(t.Competence).trim()] = t.Total;
  });
  const end = moment(competence, "YYYYMM");
  return Array.from({ length: 6 }, (_, i) => {
    const month = end.clone().subtract(5 - i, "months");
    return {
      month: SHORT_MONTHS[month.month()],
      competence: month.format("YYYYMM"),
      value: byCompetence[month.format("YYYYMM")] || 0
    };
  });
}

export function formatDays(value) {
  if (value == null) return "—";
  return Number(value).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function toRules(r) {
  const rules = r || {};
  return {
    minimumPerMonth: rules.MinimumPerMonth || 40,
    maxDaysWithoutRecord: rules.MaxDaysWithoutRecord || 45,
    riskDaysWithoutRecord: rules.RiskDaysWithoutRecord || 30,
    attentionDaysWithoutRecord: rules.AttentionDaysWithoutRecord || 10
  };
}

// ===== Tela 7: painel da equipe matricial =====
export async function getNetworkDashboard(competence) {
  const data = await apiGet(`/network/dashboard${query({ competence })}`);
  const info = competenceInfo(data.Competence);
  const rules = toRules(data.Rules);
  const indicators = data.Indicators || {};
  return {
    team: {
      id: data.Team ? data.Team.Id : null,
      name: data.Team ? data.Team.Name : "",
      region: data.Team ? data.Team.Municipality || "" : "",
      referenceMonth: info.label
    },
    competence: info.code,
    funding: FUNDING,
    rules,
    coverage: (data.Coverage || []).map((c) => ({
      municipality: c.Municipality,
      expected: c.Expected,
      enabled: c.Enabled
    })),
    assistencialTeams: (data.AssistencialTeams || []).map((t) => {
      const team = {
        id: t.TeamProfileId,
        name: t.Name,
        municipality: t.Municipality || "",
        production: t.Production,
        daysSinceRecord: t.DaysSinceRecord
      };
      return { ...team, status: situationStatus(t.Situation, team, rules) };
    }),
    indicators: {
      peopleInCp: indicators.PeopleInCp || 0,
      withCarePlanPct: indicators.WithCarePlanPct,
      withDirectivesPct: indicators.WithDirectivesPct,
      deathsWithGriefPct: indicators.DeathsWithGriefPct,
      deathsAtDesiredPlacePct: indicators.DeathsAtDesiredPlacePct,
      matrixSupports: indicators.MatrixSupportsInMonth || 0,
      pendingRequests: indicators.PendingRequests || 0,
      averageResponseDays: formatDays(indicators.AverageResponseDays)
    },
    production: lastSixMonths(data.OwnProduction, info.code)
  };
}

// ===== Tela 9: rede de matriciamento =====
export async function getMatrixSupportNetwork(competence) {
  const data = await apiGet(`/network/matrix-support${query({ competence })}`);
  const info = competenceInfo(data.Competence);
  return {
    referenceMonth: info.label,
    teams: (data.Teams || []).map((t) => ({
      unitId: t.AssistencialUnitId,
      teamId: t.TeamProfileId,
      teamName: t.Name,
      municipality: t.Municipality || "",
      averageResponseDays: t.AverageResponseDays,
      reasons: t.Reasons || {},
      total: t.Total
    })),
    insights: (data.Insights || []).map((i) => ({
      unitId: i.AssistencialUnitId,
      teamName: i.Name,
      reason: i.Reason,
      count: i.ReasonCount,
      total: i.Total,
      pct: i.SharePct,
      suggestedProcedureCode: i.SuggestedProcedureCode
    }))
  };
}

// ===== Tela 8: detalhe da equipe =====
export async function getTeamDetail(teamProfileId) {
  const data = await apiGet(`/network/teams/${teamProfileId}`);
  const team = data.Team || {};
  const art35 = data.Art35 || {};
  const production = lastSixMonths(data.Production, competenceInfo().code);
  return {
    team: {
      id: team.Id,
      type: team.TeamType,
      name: team.Name,
      municipality: team.Municipality || "",
      cnes: team.Cnes || "",
      ine: team.Ine || "",
      ibge: team.Ibge || "",
      cnpj: team.Cnpj || "",
      renewalDate: team.RenewalDate
    },
    members: (data.Members || []).map((m) => ({
      id: m.ProfessionalId,
      name: m.Name,
      cbos: m.Cbos || [],
      category: m.Category,
      weeklyHours: m.WeeklyHours,
      cns: m.Cns || "",
      certificateType: m.CertificateType || "",
      certificateHours: m.CertificateHours,
      warnings: m.Warnings || []
    })),
    production,
    art35: {
      newPatients: art35.NewPatients,
      deaths: art35.Deaths,
      directiveVersions: art35.DirectiveVersions,
      matrixSupports: art35.MatrixSupports,
      educationalActivities: art35.EducationalActivities,
      supervision: art35.Supervision,
      telehealth: art35.Telehealth,
      continuingEducation: art35.ContinuingEducation
    }
  };
}

// ===== Relatórios → aba Cuidados paliativos =====
export async function getHospitalReport(competence, teamProfileId) {
  const data = await apiGet(`/reports/hospital${query({ competence, teamProfileId })}`);
  const info = competenceInfo(data.Competence);
  const highlight = (list) => (list || []).map((p) => ({
    patientId: p.PatientId,
    name: p.Name,
    pain: p.Pain,
    date: p.Date
  }));
  const occupancy = data.Occupancy || {};
  const movement = data.Movement || {};
  return {
    team: {
      id: data.Team ? data.Team.Id : null,
      teamName: data.Team ? data.Team.Name : "",
      municipality: data.Team ? data.Team.Municipality || "" : "",
      referenceMonth: info.label
    },
    competence: info.code,
    peopleInCp: data.PeopleInCp,
    withCarePlanPct: data.WithCarePlanPct,
    withDirectivesPct: data.WithDirectivesPct,
    deathsAtDesiredPlacePct: data.DeathsAtDesiredPlacePct,
    averageMatrixResponseDays: data.AverageMatrixResponseDays,
    waitingMatrix: data.WaitingMatrix || 0,
    uncontrolledPainAtHome: highlight(data.UncontrolledPainAtHome),
    withoutDirective72h: highlight(data.WithoutDirective72h),
    withoutInteraction5Days: highlight(data.WithoutInteraction5Days),
    occupancy: {
      occupied: occupancy.Occupied || 0,
      beds: occupancy.Beds || 0,
      pct: occupancy.Pct,
      adult: { occupied: occupancy.AdultOccupied || 0, beds: occupancy.AdultBeds || 0 },
      pediatric: { occupied: occupancy.PediatricOccupied || 0, beds: occupancy.PediatricBeds || 0 }
    },
    movement: {
      averageStayDays: movement.AverageStayDays,
      exits: movement.Exits || 0,
      deaths: movement.Deaths || 0,
      discharges: movement.Discharges || 0,
      homeMonitored: movement.HomeMonitored || 0,
      homeInteracted7Days: movement.HomeInteracted7Days || 0
    },
    alertsAnswered24hPct: data.AlertsAnswered24hPct,
    readmissions30dPct: data.Readmissions30dPct,
    daysSinceRecord: data.DaysSinceRecord,
    situation: data.Situation,
    productionByProcedure: (data.ProductionByProcedure || []).map((p) => ({
      code: p.Code,
      name: p.Name,
      instrument: p.Instrument,
      total: p.Total
    })),
    production: lastSixMonths(data.Production, info.code)
  };
}

// ===== Regras da Tela 8 (Portaria GM/MS 3.681/2024) =====
// Composição mínima por tipo de equipe (carga horária semanal somada por categoria).
export const REQUIRED_COMPOSITION = {
  matricial: [
    { category: "Médico", hours: 40 },
    { category: "Enfermeiro", hours: 30 },
    { category: "Assistente social", hours: 30 },
    { category: "Psicólogo", hours: 30 }
  ],
  assistencial: [
    { category: "Médico", hours: 20 },
    { category: "Enfermeiro", hours: 30 },
    { category: "Assistente social", hours: 30 },
    { category: "Psicólogo", hours: 30 },
    { category: "Técnico de enfermagem", hours: 90 }
  ]
};

// Categorias de nível superior: precisam de certificado em CP para a renovação.
export const HIGHER_EDUCATION = ["Médico", "Enfermeiro", "Assistente social", "Psicólogo"];

// Atividades avaliadas pelo art. 35 (não são procedimentos do SIGTAP) → campo da API.
export const ART35_ACTIVITIES = [
  { _id: "cadastro", name: "Cadastro de pessoas em CP", field: "newPatients" },
  { _id: "obito", name: "Óbitos e acompanhamento pós-óbito", field: "deaths" },
  { _id: "dav", name: "Diretivas antecipadas registradas", field: "directiveVersions" },
  { _id: "supervisao", name: "Supervisão de casos", field: "supervision" },
  { _id: "matriciamento", name: "Matriciamento", field: "matrixSupports" },
  { _id: "educacao", name: "Ações educativas", field: "educationalActivities" },
  { _id: "telessaude", name: "Telessaúde", field: "telehealth" },
  { _id: "educacaoContinuada", name: "Educação continuada da equipe", field: "continuingEducation" }
];
